import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { hhmm, shortDate } from '../../lib/format';
import { IconButton, TrashIcon, UnassignIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';
import { TagsInput } from '@allrouted/ui/TagsInput';
import {
  Disclosure,
  Field,
  NumberInput,
  SettingsCard,
  StaticValue,
  TextArea,
  TextInput,
  TimeInput,
} from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import { CustomFieldsEditor, toFields, toRows, type FieldRow } from './CustomFieldsEditor';


/**
 * One order, in the three tabs the incumbent uses.
 *
 * Configuration is what was asked for, Execution is what happened, and
 * Communication is what the customer was told. The split matters more than it
 * looks: the same times appear on two of them meaning different things, and
 * flattening that into one screen is how "planned 08:00" and "arrived 09:03"
 * end up looking like the same fact.
 */

type Tab = 'configuration' | 'communication' | 'execution';

/** Their priority reads as a word; ours is a number the solver sorts on. */
const PRIORITY_BANDS = [
  { value: '25', label: 'Low' },
  { value: '50', label: 'Normal' },
  { value: '75', label: 'High' },
  { value: '100', label: 'Critical' },
];

function priorityLabel(priority: number): string {
  if (priority >= 100) return 'Critical';
  if (priority >= 75) return 'High';
  if (priority <= 25) return 'Low';
  return 'Normal';
}

const UNPINNED = '__any__';

/** hh:mm over a duration, which is how service time reads on their screen. */
function duration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function isoDate(value: unknown): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function addressLines(address: unknown): string {
  if (!address || typeof address !== 'object') return '';
  const a = address as Record<string, string | null>;
  return [a.line1, a.line2, a.city, a.region, a.postcode, a.country]
    .filter((part) => part && String(part).trim())
    .join(', ');
}

interface Order extends Record<string, unknown> {
  id: string;
  name: string;
}

export function OrderDetail({
  territoryId,
  orderId,
  generation,
  vehicles,
  onSaved,
  onAdjusting,
  pinned,
  onRemoved,
}: {
  territoryId: string;
  orderId: string;
  /** Changes when the list reloads, so an open order follows it. */
  generation: number;
  vehicles: { id: string; name: string }[];
  onSaved: () => void;
  /** Where the pin sits while editing, so the map can offer it for dragging. */
  onAdjusting: (at: { lat: number; lng: number } | null) => void;
  /** The map dragged it; adopt that position into the form. */
  pinned: { lat: number; lng: number } | null;
  /**
   * The order has left this screen -- taken off the route, or deleted.
   *
   * Told rather than handled here, because what should happen next differs by
   * where the pane is: the Orders list reloads and picks another row, and the
   * drawer on the board closes.
   */
  onRemoved?: (what: 'unassigned' | 'deleted') => void;
}) {
  const ui = useUi();
  const [tab, setTab] = useState<Tab>('configuration');
  const [order, setOrder] = useState<Order | null>(null);
  const [draft, setDraft] = useState<Order | null>(null);
  const [editing, setEditing] = useState(false);
  /* Custom fields being edited, as rows; reset from the order whenever editing is not under way. */
  const [fieldRows, setFieldRows] = useState<FieldRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /* Which removal is in flight, so the pair of buttons cannot both be pressed. */
  const [removing, setRemoving] = useState<'unassigned' | 'deleted' | null>(null);

  /**
   * Off the route, still on the day.
   *
   * The date comes from the order rather than from the screen: the Orders list
   * can be showing one day while an order belongs to another, and unassigning
   * from the wrong day would answer "no such stop" for no visible reason.
   */
  const unassign = async () => {
    if (!order) return;
    setRemoving('unassigned');
    try {
      await api.unassignOrder(territoryId, isoDate(order.scheduled_date), String(order.id));
      ui.notify(
        'ok',
        `${String(order.name)} taken off the route`,
        'It stays on this date, unassigned, and the route was retimed.',
      );
      onRemoved?.('unassigned');
    } catch (err) {
      ui.notify(
        'error',
        'Could not take it off the route',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setRemoving(null);
    }
  };

  const remove = async () => {
    if (!order) return;
    /*
     * Asked, because there is no undo. The name is in the question: a dialog
     * that says "delete this order?" is answered yes by somebody who has
     * already forgotten which row they clicked.
     */
    const ok = await ui.confirm({
      title: `Delete ${String(order.name)}?`,
      body: 'The order and its place on any route are removed. This cannot be undone.',
      confirmLabel: 'Delete order',
      danger: true,
    });
    if (!ok) return;
    setRemoving('deleted');
    try {
      await api.deleteOrder(territoryId, String(order.id));
      ui.notify('ok', `${String(order.name)} deleted`);
      onRemoved?.('deleted');
    } catch (err) {
      ui.notify(
        'error',
        'Could not delete that order',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setRemoving(null);
    }
  };

  const load = useCallback(async () => {
    /*
     * An order id can arrive from the address bar, so it can be wrong: a link
     * to something since deleted, or to a day it does not belong to. Say so in
     * the pane. Left unhandled this rejects into the console and the panel sits
     * on "Loading order." forever, which reads as the application hanging.
     */
    try {
      const row = (await api.order(territoryId, orderId)) as Order;
      setOrder(row);
      setDraft({ ...row });
      setEditing(false);
      setFailure(null);
      return row;
    } catch (err) {
      setOrder(null);
      setDraft(null);
      setFailure(err instanceof ApiError ? err.message : String(err));
      return null;
    }
  }, [territoryId, orderId]);

  useEffect(() => {
    void load();
    // Reload when the list does: leaving the panel on a stale copy after a
    // refresh is how someone reads yesterday's execution and believes it.
  }, [load, generation]);

  /*
   * The pin is offered for dragging only while the order is being edited, and
   * withdrawn on cancel, save or close. A draggable pin on a panel somebody is
   * only reading is an invitation to move a customer by accident.
   */
  useEffect(() => {
    if (!editing || !draft || draft.lat === null || draft.lng === null) {
      onAdjusting(null);
      return;
    }
    onAdjusting({ lat: Number(draft.lat), lng: Number(draft.lng) });
  }, [editing, draft, onAdjusting]);

  useEffect(() => () => onAdjusting(null), [onAdjusting]);

  /* A drag on the map is an edit to this form. */
  useEffect(() => {
    if (!pinned) return;
    setDraft((current) =>
      current && (Number(current.lat) !== pinned.lat || Number(current.lng) !== pinned.lng)
        ? { ...current, lat: pinned.lat, lng: pinned.lng }
        : current,
    );
  }, [pinned]);

  useEffect(() => {
    if (!editing) setFieldRows(toRows(order?.custom_fields));
  }, [order, editing]);

  const save = async () => {
    if (!draft) return;
    const custom = toFields(fieldRows);
    if (!custom.ok) {
      ui.notify('error', 'Check the custom fields', custom.problem);
      return;
    }
    setSaving(true);
    try {
      await api.updateOrder(territoryId, draft.id, {
        customFields: custom.fields,
        location: { lat: Number(draft.lat), lng: Number(draft.lng) },
        name: draft.name,
        type: draft.type,
        serviceTime: draft.service_time,
        priority: draft.priority,
        timeWindows: draft.time_windows,
        requiredTags: draft.required_tags,
        forbiddenTags: draft.forbidden_tags,
        requiredVehicleId: draft.required_vehicle_id,
        notes: draft.notes,
      });
      ui.notify('ok', 'Order saved', 'Replan the day for the change to reach the routes.');
      await load();
      onSaved();
    } catch (err) {
      ui.notify('error', 'Could not save order', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  if (failure) {
    return (
      <div className="banner" data-tone="warn">
        <div>
          <b>That order is not here</b>
          <p>
            {failure}. The link may point at an order that was deleted, or at a different day
            than the one selected.
          </p>
        </div>
      </div>
    );
  }

  if (!order || !draft) return <div className="empty">Loading order.</div>;

  const windows = Array.isArray(draft.time_windows)
    ? (draft.time_windows as { start: number; end: number }[])
    : [];
  const firstWindow = windows[0] ?? null;

  return (
    <>
      <div className="tabs">
        {(['configuration', 'communication', 'execution'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
          >
            {t[0]?.toUpperCase()}
            {t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'configuration' ? (
        <SettingsCard
          title={String(order.name)}
          description={`${String(order.external_id ?? 'no reference')} · ${String(order.status)}`}
          editing={editing}
          onEdit={() => setEditing(true)}
          onCancel={() => {
            setEditing(false);
            setDraft({ ...order });
          }}
          onSave={() => void save()}
          saving={saving}
          /* A long form: the header buttons scroll away before the last field. */
          saveAtFoot
          /*
           * Beside the pencil, not at the foot of the form.
           *
           * They were below fifteen fields and a disclosure, so taking an
           * order off a route meant scrolling past everything about it first.
           * Icons rather than words because they sit in a row of icons, and
           * because neither is the thing you came here to do -- the tooltip
           * says which is which, and delete still asks before it acts.
           */
          actions={
            onRemoved ? (
              <>
                <IconButton
                  label={
                    String(order.status) === 'assigned'
                      ? 'Unassign — take it off the route, keeping it on this date'
                      : 'Not on a route today'
                  }
                  disabled={removing !== null || String(order.status) !== 'assigned'}
                  onClick={() => void unassign()}
                >
                  {removing === 'unassigned' ? <span className="spinner" /> : <UnassignIcon />}
                </IconButton>
                <IconButton
                  label="Delete this order entirely — asks first"
                  tone="danger"
                  disabled={removing !== null}
                  onClick={() => void remove()}
                >
                  {removing === 'deleted' ? <span className="spinner" /> : <TrashIcon />}
                </IconButton>
              </>
            ) : null
          }
        >
          <Field label="Company">
            <StaticValue>{(order.customer_name as string) ?? ''}</StaticValue>
          </Field>
          <Field label="Eligibility & recurrence">
            <StaticValue>
              {shortDate(order.scheduled_date)}
              {Array.isArray(order.eligible_dates) && order.eligible_dates.length > 0
                ? ` (also ${(order.eligible_dates as string[]).map(shortDate).join(', ')})`
                : ''}
            </StaticValue>
          </Field>
          <Field label="Required vehicle" hint="Pins the order to one technician">
            <Select
              value={(draft.required_vehicle_id as string) ?? UNPINNED}
              options={[
                { value: UNPINNED, label: 'Any vehicle' },
                ...vehicles.map((v) => ({ value: v.id, label: v.name })),
              ]}
              onChange={(v) =>
                setDraft({ ...draft, required_vehicle_id: v === UNPINNED ? null : v })
              }
            />
          </Field>
          <Field label="Priority">
            <Select
              value={String(draft.priority)}
              placeholder={priorityLabel(Number(draft.priority))}
              options={PRIORITY_BANDS}
              onChange={(v) => setDraft({ ...draft, priority: Number(v) })}
            />
          </Field>
          <Field label="Type">
            <Select
              value={String(draft.type)}
              options={[
                { value: 'service', label: 'Service' },
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
              ]}
              onChange={(v) => setDraft({ ...draft, type: v })}
            />
          </Field>
          <Field label="Address" wide>
            <StaticValue>{addressLines(order.address)}</StaticValue>
          </Field>
          <Field label="Customer phone">
            <StaticValue>{(order.customer_phone as string) ?? ''}</StaticValue>
          </Field>
          <Field label="Customer email">
            <StaticValue>{(order.customer_email as string) ?? ''}</StaticValue>
          </Field>
          <Field label="Service time" hint="hh:mm">
            <NumberInput
              value={Math.round(Number(draft.service_time) / 60)}
              min={0}
              suffix="min"
              onChange={(v) => setDraft({ ...draft, service_time: v * 60 })}
            />
          </Field>
          <Field label="Time window">
            {editing ? (
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <TimeInput
                  seconds={firstWindow?.start ?? null}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      time_windows:
                        v === null
                          ? []
                          : [{ start: v, end: firstWindow?.end ?? 86400 }],
                    })
                  }
                />
                <span style={{ color: 'var(--text-3)' }}>to</span>
                <TimeInput
                  seconds={firstWindow?.end ?? null}
                  onChange={(v) =>
                    setDraft({
                      ...draft,
                      time_windows:
                        v === null ? [] : [{ start: firstWindow?.start ?? 0, end: v }],
                    })
                  }
                />
              </span>
            ) : (
              <StaticValue mono>
                {windows.map((w) => `${hhmm(w.start)} - ${hhmm(w.end)}`).join(', ')}
              </StaticValue>
            )}
          </Field>
          <Field label="Time window exceptions">
            <StaticValue>Not built</StaticValue>
          </Field>
          <Field label="Required tags">
            <TagsInput
              value={draft.required_tags as string[]}
              placeholder="sealed system"
              onChange={(required_tags) => setDraft({ ...draft, required_tags })}
            />
          </Field>
          <Field label="Banned tags">
            <TagsInput
              value={draft.forbidden_tags as string[]}
              onChange={(forbidden_tags) => setDraft({ ...draft, forbidden_tags })}
            />
          </Field>
          <Field label="Notes" wide>
            <TextArea
              value={(draft.notes as string) ?? ''}
              rows={3}
              onChange={(v) => setDraft({ ...draft, notes: v || null })}
            />
          </Field>

          <Disclosure label="Advanced">
            <p className="disclosure-note">
              {editing
                ? 'Drag the pin on the map to correct where this order is, or type the position here.'
                : 'The position the router uses. Edit the order to move it.'}
            </p>
            <Field label="Latitude">
              <NumberInput
                value={Number(draft.lat ?? 0)}
                step={0.000001}
                onChange={(v) => setDraft({ ...draft, lat: v })}
              />
            </Field>
            <Field label="Longitude">
              <NumberInput
                value={Number(draft.lng ?? 0)}
                step={0.000001}
                onChange={(v) => setDraft({ ...draft, lng: v })}
              />
            </Field>
          </Disclosure>

          <h3 className="section-label">Custom fields</h3>
          {editing ? (
            <CustomFieldsEditor rows={fieldRows} onChange={setFieldRows} />
          ) : (
            <CustomFields values={order.custom_fields} />
          )}
        </SettingsCard>
      ) : null}

      {/*
        * The two ways an order leaves a day, kept apart.
        *
        * Taking it off a route and deleting it are a world apart in
        * consequence and one word apart in wording, so they do not sit side by
        * side as equals: unassign is an ordinary button, delete is marked as
        * destructive and asks first. Both only on the configuration tab, which
        * is the one that is about the order rather than about what happened to
        * it.
        */}

      {tab === 'communication' ? (
        <CommunicationTab territoryId={territoryId} order={order} />
      ) : null}

      {tab === 'execution' ? (
        <ExecutionTab territoryId={territoryId} order={order} onChanged={() => void load()} />
      ) : null}
    </>
  );
}

/**
 * What actually happened, kept apart from what was asked for.
 *
 * Reported and detected are shown side by side rather than merged, because the
 * difference between them is the interesting part: a technician who forgot to
 * press the button leaves reported blank while the geofence still saw the van,
 * and collapsing the two would hide which one you are reading.
 *
 * Corrections are appended, never applied in place. Saving here posts execution
 * events from `dispatcher`, exactly as the driver app posts them from the field,
 * so the record still shows what the technician said and what somebody changed
 * it to. An edit that quietly overwrote the original would leave the event log
 * below contradicting the times above it.
 */
function ExecutionTab({
  territoryId,
  order,
  onChanged,
}: {
  territoryId: string;
  order: Order;
  onChanged: () => void;
}) {
  const ui = useUi();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const reportedIn = order.actual_arrival as number | null;
  const reportedOut = order.actual_departure as number | null;
  const detectedIn = order.detected_arrival as number | null;
  const detectedOut = order.detected_departure as number | null;
  const stopStatus = (order.stop_status as string) ?? 'pending';

  const [draftIn, setDraftIn] = useState<number | null>(reportedIn);
  const [draftOut, setDraftOut] = useState<number | null>(reportedOut);
  const [draftStatus, setDraftStatus] = useState(stopStatus);
  const [draftReason, setDraftReason] = useState((order.reason_code as string) ?? '');

  const beginEdit = () => {
    setDraftIn(reportedIn);
    setDraftOut(reportedOut);
    setDraftStatus(stopStatus);
    setDraftReason((order.reason_code as string) ?? '');
    setEditing(true);
  };

  const inAt = reportedIn ?? detectedIn;
  const outAt = reportedOut ?? detectedOut;
  const served = inAt !== null && outAt !== null ? outAt - inAt : null;

  const events = Array.isArray(order.events) ? (order.events as Record<string, unknown>[]) : [];
  const onDate = isoDate(order.plan_date ?? order.scheduled_date);
  const stopId = (order.route_stop_id as string) ?? null;

  const save = async () => {
    if (!stopId) return;
    setSaving(true);
    try {
      /*
       * Arrival first. Both events are authoritative, and a completion carries
       * the status, so posting them the other way round would leave the stop
       * reading on-site until the next refresh.
       */
      const post = (body: Record<string, unknown>) =>
        api.executionEvent(territoryId, {
          source: 'dispatcher',
          routeStopId: stopId,
          onDate,
          ...body,
        });

      let posted = 0;
      if (draftIn !== null && draftIn !== reportedIn) {
        await post({ type: 'arrived', atLocalSeconds: draftIn });
        posted += 1;
      }

      const finishing = draftStatus === 'completed' || draftStatus === 'failed';
      const departureMoved = draftOut !== null && draftOut !== reportedOut;
      const statusMoved = draftStatus !== stopStatus;
      const reasonMoved = draftReason !== ((order.reason_code as string) ?? '');

      if (finishing && (departureMoved || statusMoved || reasonMoved)) {
        await post({
          type: draftStatus === 'completed' ? 'completed' : 'failed',
          atLocalSeconds: draftOut ?? outAt ?? draftIn ?? 0,
          reasonCode: draftReason.trim() || null,
        });
        posted += 1;
      }

      if (posted === 0) {
        ui.notify('info', 'Nothing to change');
      } else {
        ui.notify(
          'ok',
          'Execution corrected',
          'Recorded as a dispatcher event, so the original report is still on the log.',
        );
      }
      setEditing(false);
      onChanged();
    } catch (err) {
      ui.notify(
        'error',
        'Could not save the correction',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Execution"
      description={`${String(order.technician_name ?? order.vehicle_name ?? 'unassigned')} · ${String(
        order.stop_status ?? 'not planned',
      )}`}
      editing={editing}
      onEdit={stopId ? beginEdit : undefined}
      onCancel={() => setEditing(false)}
      onSave={() => void save()}
      saving={saving}
    >
      <Field label="Planned arrival">
        <StaticValue mono>{hhmm(order.planned_arrival as number | null)}</StaticValue>
      </Field>
      <Field label="Stop">
        <StaticValue>
          {order.sequence !== null && order.sequence !== undefined
            ? `${String(order.sequence)} on ${String(order.vehicle_name ?? '')}`
            : ''}
        </StaticValue>
      </Field>

      <h3 className="section-label">Reported</h3>
      <Field label="Check in" hint="What the technician entered">
        {editing ? (
          <TimeInput seconds={draftIn} onChange={setDraftIn} />
        ) : (
          <StaticValue mono>{hhmm(reportedIn)}</StaticValue>
        )}
      </Field>
      <Field label="Check out">
        {editing ? (
          <TimeInput seconds={draftOut} onChange={setDraftOut} />
        ) : (
          <StaticValue mono>{hhmm(reportedOut)}</StaticValue>
        )}
      </Field>
      <Field label="Status">
        {editing ? (
          <Select
            value={draftStatus}
            options={[
              { value: 'completed', label: 'Completed' },
              { value: 'failed', label: 'Failed' },
              { value: 'on_site', label: 'On site' },
              { value: 'en_route', label: 'En route' },
              { value: 'pending', label: 'Not started' },
            ]}
            onChange={setDraftStatus}
          />
        ) : (
          <StaticValue>{stopStatus}</StaticValue>
        )}
      </Field>
      <Field label="Reason" hint="Why it failed, or a note on the correction">
        {editing ? (
          <TextInput value={draftReason} onChange={setDraftReason} />
        ) : (
          <StaticValue>{(order.reason_code as string) ?? ''}</StaticValue>
        )}
      </Field>
      {editing ? (
        <p className="disclosure-note">
          Saved as a dispatcher event rather than an edit in place, so the technician's original
          report stays on the log below. A stop cannot be moved back to Not started.
        </p>
      ) : null}

      <h3 className="section-label">Detected</h3>
      <Field label="Check in" hint="Inferred from the vehicle crossing the geofence">
        <StaticValue mono>{hhmm(detectedIn)}</StaticValue>
      </Field>
      <Field label="Check out">
        <StaticValue mono>{hhmm(detectedOut)}</StaticValue>
      </Field>
      <Field label="Calculated service time">
        <StaticValue mono>
          {served === null ? '' : `${Math.round(served / 60)} min`}
        </StaticValue>
      </Field>
      <Field label="Planned service time">
        <StaticValue mono>{duration(order.planned_service_s as number | null)}</StaticValue>
      </Field>

      <h3 className="section-label">Proofs of delivery</h3>
      <Field label="Note">
        <StaticValue>Not built</StaticValue>
      </Field>
      <Field label="Barcodes">
        <StaticValue>Not built</StaticValue>
      </Field>

      <h3 className="section-label">Custom fields at execution</h3>
      <CustomFields values={order.custom_fields} />

      <h3 className="section-label">Event log</h3>
      {events.length === 0 ? (
        <div className="empty" style={{ padding: '10px 0', textAlign: 'left' }}>
          Nothing has been reported against this order.
        </div>
      ) : (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event</th>
                <th>Source</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={String(e.id)}>
                  <td className="num">{hhmm(e.local_seconds as number)}</td>
                  <td>{String(e.type)}</td>
                  <td>{String(e.source)}</td>
                  <td>{(e.reason_code as string) ?? '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SettingsCard>
  );
}

/**
 * Whatever the upstream system carried along.
 *
 * These are integration keys -- GPS ID, Visit ID, Entry ID on the incumbent --
 * and a drop-in has to show them, because they are how someone matches a row
 * here to a row in the system that created the work.
 */
function CustomFields({ values }: { values: unknown }) {
  const entries =
    values && typeof values === 'object' ? Object.entries(values as Record<string, unknown>) : [];

  if (entries.length === 0) {
    return (
      <div className="empty" style={{ padding: '10px 0', textAlign: 'left' }}>
        No custom fields on this order.
      </div>
    );
  }

  return (
    <>
      {entries.map(([key, value]) => (
        <Field key={key} label={key}>
          <StaticValue mono>{value === null ? '' : String(value)}</StaticValue>
        </Field>
      ))}
    </>
  );
}

/**
 * What the customer has been told about this visit.
 *
 * A log. The incumbent's tab is a list of notifications actually sent, with an
 * empty state that points at the settings rather than pretending there is
 * nothing to configure -- we had built a shell that showed the customer's
 * contact details and a hardcoded "Notifications sent: None", which answered a
 * question nobody asks and hid the one they do.
 */
function CommunicationTab({
  territoryId,
  order,
}: {
  territoryId: string;
  order: Record<string, unknown>;
}) {
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .orderNotifications(territoryId, String(order.id))
      .then(({ notifications }) => {
        if (!cancelled) setRows(notifications);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [territoryId, order.id]);

  return (
    <SettingsCard
      title="Communication"
      description="What this customer has been told about this visit."
    >
      {rows === null ? (
        <div className="empty">Loading.</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <EnvelopeIllustration />
          <b>No customer notifications yet</b>
          <p>
            Notifications for this customer appear here once they are sent automatically. If you
            expected a message and do not see it, check the notification settings.
          </p>
          <a className="btn" href="#/setup/notifications">
            Notification settings
          </a>
        </div>
      ) : (
        <div className="notif-log">
          {rows.map((n) => (
            <div key={String(n.id)} className="notif-log-row" data-status={String(n.status)}>
              <div className="notif-log-head">
                <b>{String(n.subject ?? n.kind)}</b>
                <span className="mono">{new Date(String(n.sent_at)).toLocaleString()}</span>
              </div>
              <small>
                {String(n.kind)} · {String(n.status).toLowerCase()} · {String(n.recipient)}
              </small>
              {n.body_plain ? <pre>{String(n.body_plain)}</pre> : null}
            </div>
          ))}
        </div>
      )}
    </SettingsCard>
  );
}

/* Their empty state leads with a drawing; ours led with a line of grey text. */
function EnvelopeIllustration() {
  return (
    <svg width="64" height="48" viewBox="0 0 64 48" fill="none" aria-hidden="true">
      <rect x="6" y="12" width="52" height="32" rx="4" fill="var(--surface-3)"
        stroke="var(--line-strong)" strokeWidth="1.5" />
      <path d="M6 16l26 18 26-18" stroke="var(--line-strong)" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M20 4h24M24 9h16" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
